class TabbedProductBlock extends HTMLElement {
  constructor() {
    super();
    this.tabs = this.querySelectorAll(".tabbed-product-block__tab");
    this.panels = this.querySelectorAll(".tabbed-product-block__panel");
    this.activeTab = null;
    this.defaultIndex = parseInt(this.dataset.defaultIndex || "1", 10);

    this.init();
  }

  init() {
    const defaultTab = this.dataset.defaultTab || "subscribe";
    this.bindEvents();
    this.switchTab(defaultTab);
    this.initialized = true;
    this.bindProductSelection();
    this.selectFirstProductInActiveTab(this.defaultIndex);
  }

  bindEvents() {
    this.tabs.forEach((tab) => {
      tab.addEventListener("click", (e) => {
        e.preventDefault();
        const tabName = tab.getAttribute("data-tab");
        this.switchTab(tabName, this.topOptionIndex(tabName));
      });

      tab.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          const tabName = tab.getAttribute("data-tab");
          this.switchTab(tabName, this.topOptionIndex(tabName));
        }
      });
    });
  }

  /**
   * DOM index (1-based) of the option shown at the TOP of a tab's list.
   *
   * Normally that is the first one, but the subscribe panel is laid out
   * column-reverse when .reverse-subscribe-order is set, so its top card is
   * the LAST in the DOM. Picking a tab by hand should land on whatever the
   * customer sees first, so this resolves position rather than assuming
   * DOM order matches visual order.
   */
  topOptionIndex(tabName) {
    if (tabName !== "subscribe") return 1;
    if (!this.classList.contains("reverse-subscribe-order")) return 1;

    const panel = this.querySelector(
      '.tabbed-product-block__panel[data-tab="subscribe"]'
    );
    const count = panel ? panel.querySelectorAll("li").length : 0;
    return count > 0 ? count : 1;
  }

  switchTab(tabName, index = 1) {
    if (tabName === this.activeTab) return;

    this.tabs.forEach((tab) => {
      const isActive = tab.getAttribute("data-tab") === tabName;
      tab.classList.toggle("tabbed-product-block__tab--active", isActive);
      tab.setAttribute("aria-selected", isActive);
    });

    this.panels.forEach((panel) => {
      const isActive = panel.getAttribute("data-tab") === tabName;
      panel.classList.toggle("tabbed-product-block__panel--active", isActive);
    });

    this.activeTab = tabName;

    if (this.initialized) {
      this.selectFirstProductInActiveTab(index);
    }

    this.dispatchEvent(
      new CustomEvent("tabChanged", {
        detail: { activeTab: tabName },
      })
    );
  }

  setActiveTab(tabName) {
    this.switchTab(tabName);
  }

  getActiveTab() {
    return this.activeTab;
  }

  selectFirstProductInActiveTab(index = 1) {
    const activePanel = this.querySelector(
      ".tabbed-product-block__panel--active"
    );
    if (activePanel) {
      const allProducts = activePanel.querySelectorAll("li");
      if (allProducts.length > 0) {
        allProducts.forEach((product) => {
          product.classList.remove("active-product");
        });

        const targetIdx = Math.max(0, Math.min(index - 1, allProducts.length - 1));
        allProducts[targetIdx].classList.add("active-product");
      }
    }
  }

  bindProductSelection() {
    const productItems = this.querySelectorAll("li");

    productItems.forEach((item) => {
      item.addEventListener("click", (e) => {
        if (
          e.target.closest(".quick-add") ||
          e.target.closest("select") ||
          e.target.closest("input") ||
          e.target.closest("button") ||
          e.target.closest(".product-form") ||
          e.target.closest(".card-product")
        ) {
          return;
        }

        if (e.target !== item && !item.contains(e.target)) {
          return;
        }

        const currentPanel = item.closest(".tabbed-product-block__panel");
        const allItemsInPanel = currentPanel.querySelectorAll("li");
        allItemsInPanel.forEach((panelItem) => {
          panelItem.classList.remove("active-product");
        });

        item.classList.add("active-product");
      });
    });
  }
}

customElements.define("tabbed-product-block", TabbedProductBlock);

/**
 * "One Time Purchase" link, rendered under the add-to-basket button on the PDP
 * while the tab strip is hidden (see the custom_button block in
 * custom-collection-block.liquid).
 *
 * It stands in for the one-time tab rather than doing anything of its own:
 * pressing it reveals the tabs, switches to one-time and selects that panel's
 * first option. The add-to-basket button follows the selection as usual, so
 * the customer still confirms the purchase themselves.
 *
 * Delegated from document because the link sits outside <tabbed-product-block>
 * and custom-product-button.js re-renders that corner of the DOM.
 */
document.addEventListener("click", (e) => {
  const trigger =
    e.target.closest && e.target.closest("[data-one-time-purchase]");
  if (!trigger) return;

  const scope = trigger.closest(".shopify-section") || document;
  const block = scope.querySelector("tabbed-product-block");
  // No element, or it has not upgraded yet: leave the link alone so a second
  // press can still work rather than hiding a control that did nothing.
  if (!block || typeof block.setActiveTab !== "function") return;

  block.classList.remove("tabs-hidden");
  block.setActiveTab("one-time");
  // switchTab() returns early when one-time is already active, so select here
  // too — the link must land on the first option either way.
  block.selectFirstProductInActiveTab(1);

  trigger.hidden = true;
});
